// src/context/NavContext.js
//
// Single source of truth for the live fund NAV. Replaces the old pattern of
// two independent fetches (index.js's initNavNumber() + Navigation.js's own
// local fetchLatestNav() call) that each landed in a different place and
// never reactively updated component renders.
import { createContext, useContext, useEffect, useState } from "react";
import { fetchLatestNav } from "../services/Axios";
import { DEFAULT_NAV_NUMBER, setNavNumber } from "../config/config";
import { useAuth } from "./AuthContext";

const NavContext = createContext(DEFAULT_NAV_NUMBER);

export const NavProvider = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [nav, setNav] = useState(DEFAULT_NAV_NUMBER);

  useEffect(() => {
    // Don't fire until auth is ready — mirrors PositionsDataProvider so this
    // doesn't race token acquisition on first landing.
    if (!isAuthenticated || authLoading) return;

    let active = true;
    fetchLatestNav()
      .then((value) => {
        if (!active) return;
        if (typeof value === "number" && value > 0) {
          setNav(value);
          // Keep the legacy mutable config.js export in sync for the
          // non-component consumers (table column configs, calculation
          // helpers) that read NAV_NUMBER as a plain value rather than
          // through this context.
          setNavNumber(value);
        } else {
          console.warn("Backend returned invalid NAV, using default:", DEFAULT_NAV_NUMBER);
        }
      })
      .catch((error) => {
        console.warn("Failed to fetch NAV from backend, using default:", error.message);
      });

    return () => {
      active = false;
    };
  }, [isAuthenticated, authLoading]);

  return <NavContext.Provider value={nav}>{children}</NavContext.Provider>;
};

export const useNav = () => useContext(NavContext);
